import { useCallback, useMemo, useRef } from 'react';
import { useStore } from 'react-admin';

export type StoredValueParser<T> = (storedValue: unknown) => T | undefined;

const usePersistedStore = <T, >(
  key: string,
  defaultValue: T,
  parse: StoredValueParser<T>,
): [T, (value: T | ((currentValue: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useStore<unknown>(key, defaultValue);

  // Callers often build the default value on each render, keep it out of the memo dependencies
  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  const parseOrDefault = useCallback(
    (value: unknown) => parse(value) ?? defaultValueRef.current,
    [parse],
  );

  const value = useMemo(() => parseOrDefault(storedValue), [parseOrDefault, storedValue]);

  const setValue = useCallback((newValue: T | ((currentValue: T) => T)) => {
    setStoredValue((currentStoredValue: unknown) => (typeof newValue === 'function'
      ? (newValue as (currentValue: T) => T)(parseOrDefault(currentStoredValue))
      : newValue));
  }, [parseOrDefault, setStoredValue]);

  return [value, setValue];
};

export default usePersistedStore;
